import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/animations';
import { isDemoMode, demoQuotesStore, ensureDemoSoldQuotes } from '../utils/demo';
import { ServiceFactory } from '../services/factory/ServiceFactory';

interface Employer {
  id: string;
  name: string;
  brokerId: string;
  quotesCount: number;
  soldCasesCount: number;
  totalQuotes: number;
  recentQuoteDate?: string;
  status: 'active' | 'pending' | 'inactive';
}

interface Quote {
  submissionId: string;
  companyName: string;
  transperraRep: string;
  ichraEffectiveDate: string;
  pepm: string;
  status: string;
  brokerName: string;
  employerName: string;
  brokerId: string;
  employerId: string;
  agency?: string;
  s3Key: string;
}

const EmployersList: React.FC = () => {
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { } = useAuth();

  const fetchEmployers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (isDemoMode()) {
        // Use local demo data store for demo mode
        const localQuotes = demoQuotesStore.readAll();
        const soldQuotes = ensureDemoSoldQuotes();

        // Combine local and sold quotes, avoiding duplicates
        const allQuotes = [...soldQuotes, ...localQuotes].reduce((acc: Quote[], q: Quote) => {
          if (!acc.find((x: Quote) => x.submissionId === q.submissionId)) {
            // Filter out test data
            if ((q.companyName || '').toLowerCase() !== 'eggleston test' &&
                (q.employerName || '').toLowerCase() !== 'eggleston test') {
              acc.push(q);
            }
          }
          return acc;
        }, []);

        // Group quotes by employer
        const employersMap = new Map<string, Employer>();

        allQuotes.forEach(quote => {
          const employerId = quote.employerId;
          const employerName = quote.employerName || quote.companyName || employerId;

          if (!employersMap.has(employerId)) {
            employersMap.set(employerId, {
              id: employerId,
              name: employerName,
              brokerId: quote.brokerId,
              quotesCount: 0,
              soldCasesCount: 0,
              totalQuotes: 0,
              status: 'active'
            });
          }

          const employer = employersMap.get(employerId)!;
          employer.totalQuotes++;

          if (quote.status === 'Sold Case' || quote.status === 'Sold') {
            employer.soldCasesCount++;
          } else {
            employer.quotesCount++;
          }

          // Update recent quote date
          if (!employer.recentQuoteDate || quote.ichraEffectiveDate > employer.recentQuoteDate) {
            employer.recentQuoteDate = quote.ichraEffectiveDate;
          }
        });

        setEmployers(Array.from(employersMap.values()));
        setError(null);
        return;
      }

      // For production mode, use ServiceFactory to get quotes and build employers list
      const apiService = ServiceFactory.createApiService();
      const quotes = await apiService.Quote.getQuotes();

      // Group quotes by employer (similar logic to demo mode)
      const employersMap = new Map<string, Employer>();

      quotes.forEach((quote: any) => {
        const employerId = quote.employerId || quote.companyName;
        const employerName = quote.employerName || quote.companyName || employerId;

        if (!employersMap.has(employerId)) {
          employersMap.set(employerId, {
            id: employerId,
            name: employerName,
            brokerId: quote.brokerId || '',
            quotesCount: 0,
            soldCasesCount: 0,
            totalQuotes: 0,
            status: 'active'
          });
        }

        const employer = employersMap.get(employerId)!;
        employer.totalQuotes++;

        if (quote.status === 'Sold Case' || quote.status === 'Sold') {
          employer.soldCasesCount++;
        } else {
          employer.quotesCount++;
        }

        // Update recent quote date
        if (!employer.recentQuoteDate || quote.ichraEffectiveDate > employer.recentQuoteDate) {
          employer.recentQuoteDate = quote.ichraEffectiveDate;
        }
      });

      setEmployers(Array.from(employersMap.values()));
      setError(null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(`Failed to fetch employers: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployers();
  }, [fetchEmployers]);

  // Filter employers based on search term
  const filteredEmployers = useMemo(() => {
    if (!searchTerm) return employers;

    return employers.filter(employer =>
      employer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employer.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employers, searchTerm]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-seafoam border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto px-2 py-8 w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-brand-gradient rounded-brand p-6 mb-8 text-white shadow-brand"
      >
        <h1 className="text-3xl font-bold mb-2">Employers</h1>
        <p className="text-sky-100">Manage employer accounts and track their quotes and proposals</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.2 }}
        className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4"
      >
        <div className="w-full max-w-md">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search employers..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-seafoam focus:border-transparent w-full bg-white dark:bg-night-800 dark:border-night-700 dark:text-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </motion.div>

      {error && (
        <FadeIn>
          <div className="rounded-brand bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300 mb-6">
            <p className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
            <button
              onClick={() => fetchEmployers()}
              className="mt-2 px-3 py-1 text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-md transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        </FadeIn>
      )}

      {/* Employers grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredEmployers.length > 0 ? (
          filteredEmployers.map((employer, index) => (
            <motion.div
              key={employer.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index, duration: 0.3 }}
              className="bg-white dark:bg-night-800 rounded-brand shadow-brand dark:shadow-dark p-6 hover:shadow-lg dark:hover:shadow-dark-lg transition-shadow duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {employer.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    ID: {employer.id}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs font-semibold rounded-md ${
                  employer.status === 'active'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                    : employer.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
                }`}>
                  {employer.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-seafoam">{employer.quotesCount}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Active Quotes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{employer.soldCasesCount}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Sold Cases</div>
                </div>
              </div>

              {employer.recentQuoteDate && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Recent activity: {employer.recentQuoteDate}
                </p>
              )}

              <div className="flex space-x-2">
                <Link
                  to={`/employers/${employer.id}?brokerId=${employer.brokerId}`}
                  className="flex-1 bg-seafoam hover:bg-seafoam-600 text-white px-3 py-2 rounded-md text-sm text-center transition-colors duration-200"
                >
                  View Details
                </Link>
                <Link
                  to={`/employers/${employer.id}/quotes?brokerId=${employer.brokerId}`}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-night-700 dark:hover:bg-night-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded-md text-sm text-center transition-colors duration-200"
                >
                  View Quotes
                </Link>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full bg-white dark:bg-night-800 rounded-brand shadow-brand dark:shadow-dark p-8 text-center">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">
                {searchTerm ? 'No employers match your search' : 'No employers found'}
              </p>
              <p className="text-sm">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'Employers will appear here once quotes are created'
                }
              </p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EmployersList;